import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Edit, Trash2, PlayCircle, Archive, Megaphone } from 'lucide-react';

const GRADIENT_OPTIONS = [
  { k: 'yellow', label: 'Yellow', bg: 'bg-gradient-to-r from-[#FCD535] to-[#F0B90B]' },
  { k: 'green',  label: 'Green',  bg: 'bg-gradient-to-r from-[#0ECB81] to-[#0AB673]' },
  { k: 'red',    label: 'Red',    bg: 'bg-gradient-to-r from-[#F6465D] to-[#E33A50]' },
  { k: 'violet', label: 'Violet', bg: 'bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9]' },
  { k: 'blue',   label: 'Blue',   bg: 'bg-gradient-to-r from-[#4285F4] to-[#1E60D0]' },
];

const empty = {
  title: '', subtitle: '', badge: '',
  cta_label: 'Claim', cta_url: '/spot',
  gradient: 'yellow', priority: 0, publish: true,
};

// Admin sub-tab for banner CRUD. Renders inside AdminOpsPanel when
// sub === 'banners'. Any published banner shows up on the landing page
// carousel immediately via the /banners/live endpoint.
export default function BannersPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // full doc when editing; null = closed; empty = new
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/banners');
      setRows(r.data.banners || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...empty });
  const openEdit = (r) => setEditing({ ...empty, ...r });

  const save = async () => {
    if (!editing) return;
    if (!editing.title?.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      if (editing.banner_id) {
        await api.put(`/admin/banners/${editing.banner_id}`, editing);
        toast.success('Banner updated');
      } else {
        await api.post('/admin/banners', editing);
        toast.success(editing.publish ? 'Banner created & published' : 'Draft saved');
      }
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const publish = async (r) => {
    try { await api.post(`/admin/banners/${r.banner_id}/publish`); toast.success('Published'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const archive = async (r) => {
    try { await api.post(`/admin/banners/${r.banner_id}/archive`); toast.success('Archived'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete "${r.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/banners/${r.banner_id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-banners-panel">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase font-mono text-[#848E9C] flex items-center gap-1">
          <Megaphone className="w-3 h-3" /> {rows.length} banners · {rows.filter(r => r.status === 'published').length} live
        </div>
        <button onClick={openNew}
          className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold uppercase text-xs px-3 py-1.5 rounded-sm inline-flex items-center gap-1"
          data-testid="admin-banners-new">
          <Plus className="w-3 h-3" /> New Banner
        </button>
      </div>

      <div className="border border-[#2B3139] bg-[#181A20] overflow-x-auto" data-testid="admin-banners-table">
        <table className="w-full text-xs">
          <thead className="bg-[#0B0E11] text-[#848E9C] uppercase font-mono">
            <tr>
              <th className="text-left px-3 py-2">Title</th>
              <th className="text-left px-3 py-2">Badge / Color</th>
              <th className="text-left px-3 py-2">Priority</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Impressions · CTR · 7d</th>
              <th className="text-left px-3 py-2">Created</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-[#5E6673] py-6">
                {loading ? 'Loading…' : 'No banners yet. Create your first to show it on the homepage.'}
              </td></tr>
            ) : rows.map(r => (
              <tr key={r.banner_id} className="border-t border-[#2B3139]/60" data-testid={`admin-banners-row-${r.banner_id}`}>
                <td className="px-3 py-2">
                  <div className="text-white font-semibold max-w-[300px] truncate">{r.title}</div>
                  {r.subtitle && <div className="text-[10px] text-[#848E9C] max-w-[300px] truncate">{r.subtitle}</div>}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className={`w-4 h-4 rounded-sm ${GRADIENT_OPTIONS.find(g => g.k === r.gradient)?.bg || GRADIENT_OPTIONS[0].bg}`} />
                    {r.badge && <span className="text-[10px] uppercase font-mono bg-[#0B0E11] border border-[#2B3139] px-1.5 py-0.5 rounded-sm text-[#B7BDC6]">{r.badge}</span>}
                  </div>
                </td>
                <td className="px-3 py-2 font-mono text-[#B7BDC6]">{r.priority ?? 0}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] uppercase font-mono px-2 py-0.5 rounded-sm ${
                    r.status === 'published' ? 'bg-[#0ECB81]/20 text-[#0ECB81]' :
                    r.status === 'archived' ? 'bg-[#5E6673]/20 text-[#5E6673]' :
                    'bg-[#F0B90B]/20 text-[#F0B90B]'
                  }`} data-testid={`admin-banners-status-${r.banner_id}`}>{r.status}</span>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <div>
                      <div className="text-white font-mono text-xs">
                        {(r.impressions || 0).toLocaleString()} <span className="text-[#5E6673]">imp</span>
                      </div>
                      <div className="text-[10px] font-mono text-[#0ECB81]">
                        {(r.clicks || 0).toLocaleString()} clk · {r.ctr ?? 0}% CTR
                      </div>
                    </div>
                    <BannerSparkline bannerId={r.banner_id} />
                  </div>
                </td>
                <td className="px-3 py-2 text-[#5E6673]">{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</td>
                <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                  <button onClick={() => openEdit(r)} className="text-[#FCD535] hover:underline text-xs uppercase font-mono inline-flex items-center gap-0.5" data-testid={`admin-banners-edit-${r.banner_id}`}>
                    <Edit className="w-3 h-3" /> Edit
                  </button>
                  {r.status !== 'published' && (
                    <button onClick={() => publish(r)} className="text-[#0ECB81] hover:underline text-xs uppercase font-mono inline-flex items-center gap-0.5" data-testid={`admin-banners-publish-${r.banner_id}`}>
                      <PlayCircle className="w-3 h-3" /> Publish
                    </button>
                  )}
                  {r.status === 'published' && (
                    <button onClick={() => archive(r)} className="text-[#848E9C] hover:underline text-xs uppercase font-mono inline-flex items-center gap-0.5" data-testid={`admin-banners-archive-${r.banner_id}`}>
                      <Archive className="w-3 h-3" /> Archive
                    </button>
                  )}
                  <button onClick={() => del(r)} className="text-[#F6465D] hover:underline text-xs uppercase font-mono inline-flex items-center gap-0.5" data-testid={`admin-banners-delete-${r.banner_id}`}>
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Editor modal */}
      <Dialog open={!!editing} onOpenChange={v => !v && setEditing(null)}>
        <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-lg" data-testid="admin-banners-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#FCD535]">
              <Megaphone className="w-4 h-4" />
              {editing?.banner_id ? 'Edit banner' : 'New banner'}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              {/* Live preview */}
              <div className={`rounded-sm ${GRADIENT_OPTIONS.find(g => g.k === editing.gradient)?.bg || GRADIENT_OPTIONS[0].bg} px-4 py-3`} data-testid="admin-banners-preview">
                {editing.badge && (
                  <span className="text-[9px] uppercase font-mono tracking-wider bg-black/25 text-white px-1.5 py-0.5 rounded-sm mr-2">{editing.badge}</span>
                )}
                <span className="text-black font-bold text-sm">{editing.title || 'Your title here'}</span>
                {editing.subtitle && <div className="text-black/70 text-[11px] mt-0.5">{editing.subtitle}</div>}
              </div>
              <div>
                <Label className="text-[10px] uppercase text-[#848E9C]">Title *</Label>
                <Input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })}
                  maxLength={120}
                  className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white mt-1" data-testid="admin-banners-title" />
              </div>
              <div>
                <Label className="text-[10px] uppercase text-[#848E9C]">Subtitle (optional)</Label>
                <Input value={editing.subtitle || ''} onChange={e => setEditing({ ...editing, subtitle: e.target.value })}
                  maxLength={240}
                  className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white mt-1" data-testid="admin-banners-subtitle" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] uppercase text-[#848E9C]">Badge (optional)</Label>
                  <Input value={editing.badge || ''} onChange={e => setEditing({ ...editing, badge: e.target.value })}
                    maxLength={24} placeholder="NEW / -50%"
                    className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white mt-1" data-testid="admin-banners-badge" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-[#848E9C]">Priority (higher = first)</Label>
                  <Input type="number" value={editing.priority ?? 0}
                    onChange={e => setEditing({ ...editing, priority: parseInt(e.target.value) || 0 })}
                    className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white mt-1" data-testid="admin-banners-priority" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] uppercase text-[#848E9C]">CTA label</Label>
                  <Input value={editing.cta_label} onChange={e => setEditing({ ...editing, cta_label: e.target.value })}
                    maxLength={40}
                    className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white mt-1" data-testid="admin-banners-cta-label" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-[#848E9C]">CTA URL</Label>
                  <Input value={editing.cta_url} onChange={e => setEditing({ ...editing, cta_url: e.target.value })}
                    maxLength={200}
                    className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white mt-1 font-mono" data-testid="admin-banners-cta-url" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-[#848E9C]">Color theme</Label>
                <div className="flex gap-2 mt-1">
                  {GRADIENT_OPTIONS.map(g => (
                    <button key={g.k} type="button"
                      onClick={() => setEditing({ ...editing, gradient: g.k })}
                      className={`h-9 flex-1 rounded-sm ${g.bg} ${editing.gradient === g.k ? 'ring-2 ring-white' : 'opacity-70 hover:opacity-100'}`}
                      title={g.label} data-testid={`admin-banners-color-${g.k}`}
                    />
                  ))}
                </div>
              </div>
              {!editing.banner_id && (
                <label className="flex items-center gap-2 text-xs text-[#B7BDC6] cursor-pointer">
                  <input type="checkbox" checked={editing.publish ?? true}
                    onChange={e => setEditing({ ...editing, publish: e.target.checked })}
                    data-testid="admin-banners-publish-now" />
                  Publish immediately (uncheck to save as draft)
                </label>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setEditing(null)} disabled={saving}
              className="bg-[#0B0E11] border border-[#2B3139] text-[#B7BDC6] hover:bg-[#2B3139] rounded-sm"
              data-testid="admin-banners-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving}
              className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm"
              data-testid="admin-banners-save">
              {saving ? 'Saving…' : (editing?.banner_id ? 'Save changes' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Compact 7-day impressions sparkline. Impressions = area (yellow), clicks = dots (green).
function BannerSparkline({ bannerId }) {
  const [series, setSeries] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/admin/banners/${bannerId}/stats?days=7`);
        if (alive) setSeries(r.data.series || []);
      } catch (_e) { if (alive) setSeries([]); }
    })();
    return () => { alive = false; };
  }, [bannerId]);
  if (!series) return <div className="w-24 h-8 bg-[#0B0E11] border border-[#2B3139]" />;
  const imps = series.map(s => s.impressions);
  const clks = series.map(s => s.clicks);
  const maxV = Math.max(1, ...imps);
  const W = 96, H = 32, pad = 2;
  const stepX = (W - pad * 2) / Math.max(1, imps.length - 1);
  const y = v => pad + (H - pad * 2) - (v / maxV) * (H - pad * 2);
  const pts = imps.map((v, i) => `${pad + i * stepX},${y(v)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-24 h-8 bg-[#0B0E11] border border-[#2B3139] rounded-sm" preserveAspectRatio="none">
      <polygon fill="#FCD53522" points={`${pad},${H - pad} ${pts} ${W - pad},${H - pad}`} />
      <polyline fill="none" stroke="#FCD535" strokeWidth="1" points={pts} />
      {clks.map((v, i) => v > 0 && (
        <circle key={i} cx={pad + i * stepX} cy={y(imps[i])} r="1.5" fill="#0ECB81" />
      ))}
    </svg>
  );
}
